// src/components/challenges/CategoryChallenges.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Shield, Lock, Globe, Server, Database, Mail,
  ChevronLeft, Clock, Award, Target, TrendingUp, Play, Monitor, FileText, Network, LockKeyhole
} from 'lucide-react';

const CategoryChallenges = ({ apiService }) => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [completedChallengeIds, setCompletedChallengeIds] = useState([]);

  // Simulation-based challenges mapped by category
  // Helper function to apply sequential locking to challenges
  const applySequentialLocking = (challengesList, completedChallengeIds = []) => {
    return challengesList.map((challenge, index) => {
      if (index === 0) {
        // First challenge is always unlocked
        return { ...challenge, isLocked: false, lockReason: null };
      }
      
      const previousChallenge = challengesList[index - 1];
      const isPreviousCompleted = completedChallengeIds.includes(previousChallenge.id);
      
      return {
        ...challenge,
        isLocked: !isPreviousCompleted,
        lockReason: !isPreviousCompleted ? `Complete "${previousChallenge.title}" first` : null
      };
    });
  };

  const simulationChallenges = {
    'password_security': [
      {
        id: 'password-strength-simulator',
        title: 'Password Strength Simulator',
        description: 'Create and test passwords against real-world attack scenarios',
        simulationType: 'interactive',
        difficulty: 'beginner',
        estimatedTime: '10 min',
        xpReward: 100,
        icon: Lock,
        path: '/simulation/password',
        simulationPath: '/simulation/os?mode=password',
        completed: false
      },
      {
        id: 'credential-stuffing-defense',
        title: 'Credential Stuffing Defense',
        description: 'Defend against automated login attacks in real-time',
        simulationType: 'scenario',
        difficulty: 'intermediate',
        estimatedTime: '15 min',
        xpReward: 150,
        icon: Shield,
        path: '/simulation/web',
        completed: false
      },
      {
        id: 'mfa-implementation',
        title: 'Multi-Factor Authentication Lab',
        description: 'Implement and test various MFA methods',
        simulationType: 'lab',
        difficulty: 'intermediate',
        estimatedTime: '20 min',
        xpReward: 200,
        icon: Lock,
        path: '/simulation/os',
        completed: false
      }
    ],
    'phishing_social_engineering': [
      {
        id: 'phishing-email-simulator',
        title: 'Phishing Email Simulator',
        description: 'Identify phishing emails in a realistic inbox environment',
        simulationType: 'interactive',
        difficulty: 'beginner',
        estimatedTime: '10 min',
        xpReward: 100,
        icon: Mail,
        path: '/simulation/phishing',
        completed: false
      },
      {
        id: 'social-engineering-scenarios',
        title: 'Social Engineering Scenarios',
        description: 'Navigate through realistic social engineering attacks',
        simulationType: 'scenario',
        difficulty: 'intermediate',
        estimatedTime: '25 min',
        xpReward: 250,
        icon: Shield,
        path: '/simulation/phishing?scenario=advanced',
        completed: false
      },
      {
        id: 'spear-phishing-advanced',
        title: 'Advanced Spear Phishing',
        description: 'Detect targeted attacks designed specifically for you',
        simulationType: 'advanced',
        difficulty: 'advanced',
        estimatedTime: '30 min',
        xpReward: 300,
        icon: Mail,
        path: '/simulation/phishing?difficulty=advanced',
        completed: false
      }
    ],
    'network_security': [
      {
        id: 'network-traffic-analyzer',
        title: 'Network Traffic Analyzer',
        description: 'Analyze network packets to identify threats',
        simulationType: 'interactive',
        difficulty: 'intermediate',
        estimatedTime: '20 min',
        xpReward: 200,
        icon: Network,
        simulationPath: '/simulation/network',
        path: '/simulation/network',
        completed: false
      },
      {
        id: 'firewall-configuration',
        title: 'Firewall Configuration Lab',
        description: 'Configure firewall rules to protect a network',
        simulationType: 'lab',
        difficulty: 'advanced',
        estimatedTime: '35 min',
        xpReward: 350,
        icon: Shield,
        path: '/simulation/network?mode=firewall',
        completed: false
      },
      {
        id: 'intrusion-detection',
        title: 'Intrusion Detection System',
        description: 'Monitor and respond to network intrusions',
        simulationType: 'scenario',
        difficulty: 'advanced',
        estimatedTime: '40 min',
        xpReward: 400,
        icon: Monitor,
        path: '/simulation/network?mode=ids',
        completed: false
      }
    ],
    'malware_ransomware': [
      {
        id: 'malware-sandbox',
        title: 'Malware Analysis Sandbox',
        description: 'Safely analyze malware behavior in isolated environment',
        simulationType: 'lab',
        difficulty: 'intermediate',
        estimatedTime: '25 min',
        xpReward: 250,
        icon: Monitor,
        simulationPath: '/simulation/file-system?mode=malware',
        path: '/simulation/file-system?mode=malware',
        completed: false
      },
      {
        id: 'ransomware-response',
        title: 'Ransomware Response Drill',
        description: 'Practice incident response for ransomware attacks',
        simulationType: 'scenario',
        difficulty: 'advanced',
        estimatedTime: '45 min',
        xpReward: 450,
        icon: Shield,
        path: '/simulation/os?scenario=ransomware',
        completed: false
      },
      {
        id: 'file-system-monitor',
        title: 'File System Security Monitor',
        description: 'Detect and prevent unauthorized file modifications',
        simulationType: 'interactive',
        difficulty: 'intermediate',
        estimatedTime: '20 min',
        xpReward: 200,
        icon: FileText,
        path: '/simulation/file-system',
        completed: false
      }
    ],
    'data_protection': [
      {
        id: 'encryption-lab',
        title: 'Encryption Laboratory',
        description: 'Learn and implement various encryption methods',
        simulationType: 'lab',
        difficulty: 'intermediate',
        estimatedTime: '30 min',
        xpReward: 300,
        icon: Lock,
        path: '/simulation/os?mode=encryption',
        completed: false
      },
      {
        id: 'data-classification',
        title: 'Data Classification Exercise',
        description: 'Identify and classify sensitive data properly',
        simulationType: 'interactive',
        difficulty: 'beginner',
        estimatedTime: '15 min',
        xpReward: 150,
        icon: Database,
        path: '/simulation/file-system?mode=classification',
        completed: false
      },
      {
        id: 'privacy-compliance',
        title: 'Privacy Compliance Simulator',
        description: 'Navigate GDPR and privacy regulations',
        simulationType: 'scenario',
        difficulty: 'advanced',
        estimatedTime: '40 min',
        xpReward: 400,
        icon: Shield,
        path: '/simulation/web?scenario=privacy',
        completed: false
      }
    ],
    'incident_response': [
      {
        id: 'incident-commander',
        title: 'Incident Commander',
        description: 'Lead incident response in real-time crisis scenarios',
        simulationType: 'scenario',
        difficulty: 'advanced',
        estimatedTime: '50 min',
        xpReward: 500,
        icon: Server,
        path: '/simulation/os?scenario=incident',
        completed: false
      },
      {
        id: 'forensics-lab',
        title: 'Digital Forensics Lab',
        description: 'Investigate security breaches and collect evidence',
        simulationType: 'lab',
        difficulty: 'advanced',
        estimatedTime: '60 min',
        xpReward: 600,
        icon: Monitor,
        path: '/simulation/file-system?mode=forensics',
        completed: false
      },
      {
        id: 'recovery-procedures',
        title: 'System Recovery Procedures',
        description: 'Practice disaster recovery and system restoration',
        simulationType: 'interactive',
        difficulty: 'intermediate',
        estimatedTime: '30 min',
        xpReward: 300,
        icon: Server,
        path: '/simulation/os?mode=recovery',
        completed: false
      }
    ]
  };

  // Category metadata
  const categoryData = {
    'password_security': {
      title: 'Password Security',
      description: 'Master password creation, management, and authentication best practices through interactive simulations',
      icon: Lock,
      color: 'blue',
      bgGradient: 'from-blue-600 to-blue-800',
      totalXP: 450
    },
    'phishing_social_engineering': {
      title: 'Phishing & Social Engineering',
      description: 'Learn to identify and prevent social engineering attacks in realistic scenarios',
      icon: Mail,
      color: 'red',
      bgGradient: 'from-red-600 to-red-800',
      totalXP: 650
    },
    'network_security': {
      title: 'Network Security',
      description: 'Understand network threats and security measures through hands-on labs',
      icon: Globe,
      color: 'green',
      bgGradient: 'from-green-600 to-green-800',
      totalXP: 950
    },
    'malware_ransomware': {
      title: 'Malware & Ransomware',
      description: 'Detect and respond to malicious software threats in controlled environments',
      icon: Shield,
      color: 'purple',
      bgGradient: 'from-purple-600 to-purple-800',
      totalXP: 900
    },
    'data_protection': {
      title: 'Data Protection',
      description: 'Secure sensitive information and maintain privacy through practical exercises',
      icon: Database,
      color: 'indigo',
      bgGradient: 'from-indigo-600 to-indigo-800',
      totalXP: 850
    },
    'incident_response': {
      title: 'Incident Response',
      description: 'Learn proper procedures for security incidents through immersive simulations',
      icon: Server,
      color: 'orange',
      bgGradient: 'from-orange-600 to-orange-800',
      totalXP: 1400
    }
  };

  // Fetch completed challenges from localStorage or API
  useEffect(() => {
    const fetchCompletedChallenges = () => {
      try {
        // Try to get from localStorage first
        const storedCompleted = localStorage.getItem(`completed_challenges_${category}`);
        if (storedCompleted) {
          setCompletedChallengeIds(JSON.parse(storedCompleted));
        }
      } catch (error) {
        console.error('Error fetching completed challenges:', error);
      }
    };

    fetchCompletedChallenges();
  }, [category]);

  useEffect(() => {
    const info = categoryData[category] || {
      title: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      description: 'Complete challenges in this category',
      icon: Shield,
      color: 'gray',
      bgGradient: 'from-gray-600 to-gray-800',
      totalXP: 0
    };
    setCategoryInfo(info);
  }, [category]);


  const getDifficultyColor = (difficulty) => {
    const colors = {
      'beginner': 'text-green-400 bg-green-900/30',
      'intermediate': 'text-yellow-400 bg-yellow-900/30',
      'advanced': 'text-red-400 bg-red-900/30'
    };
    return colors[difficulty] || colors['beginner'];
  };

const handleStartSimulation = (challenge) => {
  // Prevent starting locked challenges
  if (challenge.isLocked) {
    console.log("Challenge is locked:", challenge.lockReason);
    alert(challenge.lockReason || "This challenge is locked. Complete previous challenges first.");
    return;
  }
  
  sessionStorage.setItem('currentChallenge', JSON.stringify({
    id: challenge.id,
    title: challenge.title,
    category: category,
    xpReward: challenge.xpReward,
    difficulty: challenge.difficulty
  }));  // FIXED: Complete object structure for JSON.stringify
  navigate(challenge.path);
};


  const getSimulationTypeIcon = (type) => {
    const icons = {
      'interactive': Play,
      'scenario': Target,
      'lab': Monitor,
      'advanced': Shield
    };
    return icons[type] || Play;
  };

  const baseChallenges = simulationChallenges[category] || [];
  // Apply sequential locking based on completed challenges
  const challenges = applySequentialLocking(baseChallenges, completedChallengeIds);
  const IconComponent = categoryInfo?.icon || Shield;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#102c4a] to-[#164f3f] text-white">
      {/* Header */}
      <div className={`bg-gradient-to-br ${categoryInfo?.bgGradient} p-8`}>
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/challenges')}
            className="flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ChevronLeft size={20} className="mr-1" />
            Back to Challenges
          </button>
          
          <div className="flex items-center mb-4">
            <div className="p-4 bg-white/10 rounded-lg mr-4">
              <IconComponent size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold mb-2">{categoryInfo?.title}</h1>
              <p className="text-white/80">{categoryInfo?.description}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-6 mt-6 text-sm">
            <div className="flex items-center">
              <Target size={16} className="mr-2" />
              {challenges.length} Simulations
            </div>
            <div className="flex items-center">
              <Award size={16} className="mr-2" />
              {categoryInfo?.totalXP} Total XP
            </div>
          </div>
        </div>
      </div>

      {/* Simulation Cards */}
      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {challenges.map((challenge) => {
            const TypeIcon = getSimulationTypeIcon(challenge.simulationType);
            const ChallengeIcon = challenge.icon;
            
            return (
              <div
                key={challenge.id}
                className={`relative bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden transition-all ${
                  challenge.isLocked 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-[rgba(255,255,255,0.1)] cursor-pointer'
                } group`}
                onClick={() => handleStartSimulation(challenge)}
              >
                {/* Lock Overlay for Locked Challenges */}
                {challenge.isLocked && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
                    <LockKeyhole size={48} className="text-gray-400 mb-3" />
                    <p className="text-gray-300 text-sm text-center px-4 font-medium">
                      {challenge.lockReason}
                    </p>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg group-hover:bg-[rgba(255,255,255,0.1)] transition-colors">
                      <ChallengeIcon size={24} className="text-blue-400" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <TypeIcon size={16} className="text-gray-400" />
                      <span className="text-xs text-gray-400 capitalize">{challenge.simulationType}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold mb-2">{challenge.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{challenge.description}</p>
                  
                  <div className="flex items-center justify-between text-sm mb-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyColor(challenge.difficulty)}`}>
                      {challenge.difficulty}
                    </span>
                    <div className="flex items-center space-x-4 text-gray-400">
                      <span className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {challenge.estimatedTime}
                      </span>
                      <span className="flex items-center text-purple-400">
                        <Award size={14} className="mr-1" />
                        +{challenge.xpReward} XP
                      </span>
                    </div>
                  </div>
                  
                  <button 
                    className={`w-full py-2 rounded-lg transition-colors text-sm font-medium flex items-center justify-center ${
                      challenge.isLocked 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white group'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartSimulation(challenge);
                    }}
                    disabled={challenge.isLocked}
                  >
                    {challenge.isLocked ? (
                      <>
                        <LockKeyhole size={16} className="mr-2" />
                        Locked
                      </>
                    ) : (
                      <>
                        <Play size={16} className="mr-2 group-hover:scale-110 transition-transform" />
                        Start Simulation
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        
        {challenges.length === 0 && (
          <div className="text-center py-12">
            <Shield size={64} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Simulations Available</h3>
            <p className="text-gray-400">Check back later for new simulations in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryChallenges;